import { memo } from 'react';
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from 'chart.js';
import { Pie } from 'react-chartjs-2';
import styled from 'styled-components';
import { Spin } from 'antd';

ChartJS.register(ArcElement, Tooltip, Legend);

const StyledWrapperChart = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  height: 100%;
`;

const StyledChartContainer = styled.div`
  width: 60%;
  height: 260px;
`;

const StyledLegendContainer = styled.div`
  width: 35%;
  padding-left: 10px;
  max-height: 260px;
  overflow-y: auto;
  overflow-x: auto;

  /* Custom scrollbar styling */
  &::-webkit-scrollbar {
    width: 6px;
    height: 6px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: #cdd5df;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #a0a8b1;
  }
`;

const StyledLegendItem = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 12px;
  padding-right: 8px;
  white-space: nowrap;
  min-width: max-content;
`;

const StyledColorBox = styled.div<{ color: string }>`
  min-width: 16px;
  width: 16px;
  height: 16px;
  border-radius: 4px;
  background-color: ${(props) => props.color};
  margin-right: 8px;
  flex-shrink: 0;
`;

const StyledLegendText = styled.div`
  font-size: 13px;
  color: #333;
  font-weight: 500;
  white-space: nowrap;
`;

const StyledLegendValue = styled.div`
  font-size: 13px;
  color: #666;
  margin-left: 8px;
  font-weight: 500;
  flex-shrink: 0;
`;

function HeadcountSummaryChart({ loading, dataChart }: any) {
  // Use the actual data from props or fallback to sample data
  const defaultData = {
    labels: ['Store A', 'Store B', 'Store C', 'HQ'],
    datasets: [
      {
        label: 'Headcount',
        data: [15, 16, 3, 19],
        backgroundColor: [
          'rgb(68, 114, 196)',
          'rgb(237, 125, 49)',
          'rgb(165, 165, 165)',
          'rgb(38, 68, 120)',
        ],
        borderColor: ['rgb(246, 250, 253)'],
        borderWidth: 2,
      },
    ],
  };

  // Ensure we have a valid data structure
  const chartData =
    dataChart &&
    dataChart.datasets &&
    Array.isArray(dataChart.datasets) &&
    dataChart.datasets.length > 0
      ? dataChart
      : defaultData;

  const options = {
    plugins: {
      legend: {
        display: false, // Hide the default legend as we'll create our own
      },
      tooltip: {
        callbacks: {
          label: function (context: any) {
            const label = context.label || '';
            const value = context.raw;
            return `${label}: ${value.toLocaleString()}`;
          },
        },
      },
    },
    maintainAspectRatio: false,
  };

  // Safely extract data for calculations and rendering
  const dataset = chartData.datasets[0] || { data: [], backgroundColor: [] };
  const labels = chartData.labels || [];
  const data = dataset.data || [];
  const backgroundColors = dataset.backgroundColor || [];

  // Calculate total for percentage
  const total = data.reduce((sum: number, value: number) => sum + (value || 0), 0);

  return (
    <Spin spinning={loading}>
      <StyledWrapperChart>
        <StyledChartContainer>
          <Pie data={chartData} options={options} />
        </StyledChartContainer>

        <StyledLegendContainer>
          {labels.map((label: string, index: number) => {
            const value = data[index] || 0;

            return (
              <StyledLegendItem key={index}>
                <StyledColorBox color={backgroundColors[index] || '#CCCCCC'} />
                <StyledLegendText>{label}</StyledLegendText>
                <StyledLegendValue>{value.toLocaleString()}</StyledLegendValue>
              </StyledLegendItem>
            );
          })}
        </StyledLegendContainer>
      </StyledWrapperChart>
    </Spin>
  );
}

export default memo(HeadcountSummaryChart);
